import { useState } from 'react';
import { SnackbarSeverityType } from '../../../enums';
import academicTermsApis from '../../../apis/academicTerms';
import { AcademicTerm, AcademicYearTermsRequestBody } from '../types';

interface UseAcademicTermsMutationsProps {
  school: string;
  academicYear: string;
  selectedGrade: string;
  selectedDivision: string;
  academicTerms: { termId: string; orderSequence: number }[];
  onShowToast: (msg: string, severity: SnackbarSeverityType) => void;
  refetch: () => void;
  handleDeleteModalClose: (value: boolean) => void;
}

export const useAcademicTermsMutations = ({
  school,
  academicYear,
  selectedGrade,
  selectedDivision,
  academicTerms,
  onShowToast,
  refetch,
  handleDeleteModalClose
}: UseAcademicTermsMutationsProps) => {
  const [selectedAcademicTerm, setSelectedAcademicTerm] =
    useState<AcademicTerm | null>(null);

  const handleDeleteConfirm = async () => {
    if (!selectedAcademicTerm?.id) {
      onShowToast(
        'No academic term selected for deletion.',
        SnackbarSeverityType.error
      );
      return;
    }

    try {
      const payload: AcademicYearTermsRequestBody = {
        academicYearTerms: [
          {
            academicYear,
            grade: selectedGrade,
            division: selectedDivision,
            terms: academicTerms.filter(
              (t) => t.termId !== selectedAcademicTerm.id
            ),
            removedTerms: [selectedAcademicTerm.id]
          }
        ]
      };

      const response: any = await academicTermsApis.upsertAYTerms(
        school,
        payload
      );
      const alreadyAssignedTermError = response.unsuccessfulRecords?.[0];

      onShowToast(
        alreadyAssignedTermError?.error ||
          'Academic Term deleted successfully!',
        alreadyAssignedTermError
          ? SnackbarSeverityType.error
          : SnackbarSeverityType.success
      );

      refetch();
    } catch (error) {
      console.error('Error deleting academic term:', error);
      onShowToast(
        'Failed to delete academic term.',
        SnackbarSeverityType.error
      );
    } finally {
      handleDeleteModalClose(false);
      setSelectedAcademicTerm(null);
    }
  };

  return {
    selectedAcademicTerm,
    setSelectedAcademicTerm,
    handleDeleteConfirm
  };
};
